import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Globe,
  Smartphone,
  ShoppingCart,
  Search,
  Wrench,
  Palette,
  Zap,
  Shield,
  Users,
  Target,
  CheckCircle2,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const services = [
  {
    icon: Globe,
    title: 'Création de site internet',
    description: 'Sites vitrines, institutionnels et landing pages modernes et performants.',
    href: '/services/site-internet',
  },
  {
    icon: Smartphone,
    title: 'Applications',
    description: 'Applications web et mobiles sur mesure pour vos besoins métier.',
    href: '/services/applications',
  },
  {
    icon: ShoppingCart,
    title: 'E-commerce',
    description: 'Boutiques en ligne optimisées pour la conversion et la croissance.',
    href: '/services/e-commerce',
  },
  {
    icon: Search,
    title: 'SEO / Référencement',
    description: 'Stratégies SEO pour améliorer votre visibilité sur Google.',
    href: '/services/seo',
  },
  {
    icon: Wrench,
    title: 'Maintenance',
    description: 'Support technique, mises à jour et optimisations continues.',
    href: '/services/maintenance',
  },
  {
    icon: Palette,
    title: 'Branding',
    description: 'Identité visuelle et design graphique qui marquent les esprits.',
    href: '/services/branding',
  },
];

const advantages = [
  {
    icon: Zap,
    title: 'Performance optimale',
    description: "Sites ultra-rapides optimisés pour l'expérience utilisateur et le SEO.",
  },
  {
    icon: Shield,
    title: 'Sécurité renforcée',
    description: 'Protection de vos données et de celles de vos clients.',
  },
  {
    icon: Users,
    title: 'Accompagnement dédié',
    description: 'Un interlocuteur unique pour un suivi personnalisé.',
  },
  {
    icon: Target,
    title: 'Orienté résultats',
    description: 'Solutions conçues pour générer des conversions.',
  },
];

const stats = [
  { value: '10+', label: 'Projets livrés' },
  { value: '90%', label: 'Clients satisfaits' },
  { value: '2 ans', label: "D'expérience" },
  { value: '24h-48h', label: 'Temps de réponse' },
];

const testimonials = [
  {
    content: 'Weblax a transformé notre présence en ligne. Notre nouveau site est non seulement magnifique mais aussi très performant.',
    author: 'Marie Dupont',
    role: 'CEO, TechStart',
    rating: 5,
  },
  {
    content: 'Une équipe professionnelle et réactive. Le résultat a dépassé nos attentes et notre trafic a augmenté de 200%.',
    author: 'Pierre Martin',
    role: 'Fondateur, EcoShop',
    rating: 5,
  },
  {
    content: 'Collaboration fluide et résultat impeccable. Je recommande Weblax pour tout projet digital.',
    author: 'Sophie Laurent',
    role: 'Directrice Marketing, Luxe&Co',
    rating: 5,
  },
];

const process = [
  {
    step: '01',
    title: 'Découverte',
    description: 'Analyse de vos besoins, objectifs et de votre marché.',
  },
  {
    step: '02',
    title: 'Conception',
    description: "Création des maquettes et de l'architecture du projet.",
  },
  {
    step: '03',
    title: 'Développement',
    description: 'Réalisation technique avec les meilleures technologies.',
  },
  {
    step: '04',
    title: 'Lancement',
    description: 'Mise en ligne, tests et optimisations finales.',
  },
];

export const Home = () => {
  const pageTitle =
    'Weblax | Création de site internet, SEO, e-commerce et solutions digitales';
  const pageDescription =
    'Weblax accompagne les entreprises, indépendants et marques dans la création de site internet, le référencement SEO, le e-commerce, les applications web et la stratégie digitale.';
  const pageUrl = 'https://weblax.fr/';
  const pageImage = 'https://weblax.fr/logo-weblax.png';

  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Weblax',
    url: 'https://weblax.fr',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://weblax.fr/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weblax',
    url: 'https://weblax.fr',
      logo: 'https://weblax.fr/logo.png',
    email: 'contact@weblax.fr',
    telephone: '+33685802016',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
    sameAs: [],
  };

  const professionalServiceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Weblax',
    image: pageImage,
    url: 'https://weblax.fr',
    telephone: '+33685802016',
    email: 'contact@weblax.fr',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
    areaServed: 'FR',
    description:
      'Agence digitale spécialisée en création de site internet, SEO, e-commerce, applications web, maintenance et branding.',
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="création site internet, agence web, SEO, référencement naturel, e-commerce, développement web, application web, site vitrine, branding, maintenance site web, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(websiteStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(professionalServiceStructuredData)}
        </script>
      </Helmet>

      <div data-testid="home-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-32 lg:py-40">
              <div className="max-w-4xl">
                <p className="weblax-caption text-primary mb-4 animate-fade-in">
                  Agence digitale créative
                </p>
                <h1 className="weblax-heading-display mb-6 animate-fade-in">
                  Transformez vos idées en{' '}
                  <span className="weblax-gradient-text">succès digital</span>
                </h1>
                <p className="weblax-body-lg max-w-2xl mb-8 animate-fade-in animation-delay-200">
                  Weblax accompagne les entreprises, indépendants et marques dans la création
                  de sites internet, solutions digitales et stratégies performantes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-400">
                  <Link to="/devis">
                    <Button data-testid="hero-cta-devis" className="weblax-btn-primary">
                      Demander un devis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/realisations">
                    <Button
                      data-testid="hero-cta-realisations"
                      variant="outline"
                      className="weblax-btn-secondary"
                    >
                      Voir nos réalisations
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 border-y border-border bg-card">
          <div className="weblax-container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-primary mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="weblax-section" data-testid="services-section">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Nos services</p>
              <h2 className="weblax-heading-1 mb-4">
                Des solutions digitales complètes
              </h2>
              <p className="weblax-body-lg">
                De la création de votre site à votre stratégie marketing,
                nous vous accompagnons à chaque étape.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Link key={index} to={service.href}>
                  <Card className="weblax-card-interactive h-full group">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <service.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/services">
                <Button variant="outline" className="weblax-btn-secondary">
                  Découvrir tous nos services
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="weblax-caption text-primary mb-3">Pourquoi Weblax</p>
                <h2 className="weblax-heading-1 mb-6">
                  Nos atouts, votre avantage
                </h2>
                <p className="weblax-body-lg mb-8">
                  Nous combinons expertise technique, créativité et accompagnement
                  personnalisé pour vous offrir des solutions digitales qui font la différence.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  {advantages.map((advantage, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <advantage.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">{advantage.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {advantage.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="weblax-heading-3 mb-2">Excellence garantie</h3>
                    <p className="text-muted-foreground">
                      Chaque projet est traité avec le plus grand soin
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Notre méthode</p>
              <h2 className="weblax-heading-1 mb-4">
                Un processus éprouvé
              </h2>
              <p className="weblax-body-lg">
                Une méthodologie claire et transparente pour des projets réussis.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {process.map((step, index) => (
                <div key={index} className="relative">
                  <div className="weblax-card text-center">
                    <span className="text-5xl font-bold text-primary/20 mb-4 block">
                      {step.step}
                    </span>
                    <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-border" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="weblax-section bg-card" data-testid="testimonials-section">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Témoignages</p>
              <h2 className="weblax-heading-1 mb-4">
                Ce que nos clients disent
              </h2>
              <p className="weblax-body-lg">
                La satisfaction de nos clients est notre meilleure récompense.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="weblax-card">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                      ))}
                    </div>
                    <p className="text-foreground mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    <div>
                      <p className="font-semibold">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 lg:p-16 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                  Prêt à lancer votre projet ?
                </h2>
                <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                  Discutons ensemble de vos objectifs et trouvons la solution idéale pour votre entreprise.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button
                      data-testid="cta-devis-bottom"
                      className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium"
                    >
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8 py-3 rounded-full"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;